import { z } from 'zod/v4';

import {
    animationPluginShapeLegacy,
    buttonHorizontalPositionSchema,
    buttonSizeSchema,
    extendedPositionPluginShapeLegacy,
    visibilityPluginShapeLegacy,
    widgetPositionPluginShapeLegacy,
} from '../../../common/schemas/legacy';

import { buttonItemSchema } from './item';

/**
 * Data shape for the **default-placement** button variant — the legacy "drop
 * into a normal column" form. This is the canonical button data schema; the
 * zero variant below derives from it via `.extend()`.
 *
 * `mobileHorizontalAlign`/`tabletHorizontalAlign` are `nullish()` (the host
 * form types mark them optional and old data may omit them — consumers fall
 * back to `horizontalAlign`). `isSynchronizeStyle` is nullable: very old
 * widgets persisted `null` and the editor treats that as `true`.
 *
 * Unlike video's default variant (which carries only the
 * `horizontalPosition`/`maxWidth` shorthand), the button widget has always
 * carried the simple per-device position grid; it's `nullish()` because
 * widgets persisted before the position group shipped omit it.
 */
export const buttonLegacyDefaultDataSchema = z.object({
    buttons: z
        .array(buttonItemSchema)
        .describe('The buttons in this group — this single widget can hold one or several buttons rendered together.'),
    horizontalIndent: buttonSizeSchema,
    horizontalAlign: buttonHorizontalPositionSchema,
    mobileHorizontalAlign: buttonHorizontalPositionSchema.nullish(),
    tabletHorizontalAlign: buttonHorizontalPositionSchema.nullish(),
    isSynchronizeStyle: z.boolean().nullable(),
    ...visibilityPluginShapeLegacy,
    ...animationPluginShapeLegacy,
    ...widgetPositionPluginShapeLegacy,
});

/**
 * Data shape for the **zero-block-placement** button variant — the default
 * schema with `position` overridden (zod v4 `.extend()` overwrites existing
 * keys) to the full extended-position grid (per-device
 * width/height/x/y/rotate/zIndex). Zero blocks post-date every other button
 * feature, so the extended position is required — a zero-tagged button
 * without it is a validation error by design.
 *
 * Mirrors the FormGroup produced by `getZeroButtonWidgetData` in the host app.
 */
export const buttonLegacyZeroDataSchema = buttonLegacyDefaultDataSchema.extend({
    ...extendedPositionPluginShapeLegacy,
});
